import { useCallback, useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import UncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button as MuiButton,
  Typography,
} from "@mui/material";

import type { InterviewWithParticipants } from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import { InterviewFormView } from "../admission/InterviewManagement/InterviewManagementDialog.js";

// ============================================================================
// Props
// ============================================================================

interface InterviewLinkingStepProps {
  formContextId: string;
  /** Currently selected interview ID (controlled) */
  selectedInterviewId: string | null;
  /** Called when the selection changes */
  onSelectedInterviewIdChange: (id: string | null) => void;
}

// ============================================================================
// Component
// ============================================================================

export function InterviewLinkingStep({
  formContextId,
  selectedInterviewId,
  onSelectedInterviewIdChange,
}: InterviewLinkingStepProps) {
  const [interviews, setInterviews] = useState<
    InterviewWithParticipants[] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const loadInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.interviews.getForFormContext(formContextId);
      setInterviews(response.data);
    } catch {
      setInterviews([]);
    } finally {
      setLoading(false);
    }
  }, [formContextId]);

  useEffect(() => {
    void loadInterviews();
  }, [loadInterviews]);

  // Filter out interviews that already have a confirmed or pending-confirmed
  // participant — they can't accept another doctor.
  const availableInterviews = useMemo(
    () =>
      (interviews ?? []).filter(
        (i) =>
          i.status !== "cancelled" &&
          !i.participants.some(
            (p) => p.status === "confirmed" || p.status === "pending-confirmed",
          ),
      ),
    [interviews],
  );

  // Build a label map based on created-date ordering across ALL non-cancelled
  // interviews so numbering stays consistent with the main AMHP view.
  const interviewLabelMap = useMemo(() => {
    const sorted = (interviews ?? [])
      .filter((i) => i.status !== "cancelled")
      .sort((a, b) => a.created.localeCompare(b.created));
    return new Map(
      sorted.map((i, idx) => [i.id, `MHA Assessment ${String(idx + 1)}`]),
    );
  }, [interviews]);

  const handleInterviewCreated = async () => {
    setShowCreateDialog(false);
    // Reload and auto-select the new interview
    const response = await api.interviews.getForFormContext(formContextId);
    setInterviews(response.data);
    const active = response.data.filter((i) => i.status !== "cancelled");
    const lastInterview = active.at(-1);
    if (lastInterview) {
      onSelectedInterviewIdChange(lastInterview.id);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
        }}
      >
        <CircularProgress size={24} />
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mt: 1,
          }}
        >
          Loading assessments…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Link to an Assessment (optional)
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 1.5,
        }}
      >
        Select an assessment to add the invited doctor as a pre-confirmed
        participant, or skip this step.
      </Typography>
      {availableInterviews.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 1,
            }}
          >
            No assessments available. Create one to link the doctor.
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          mb: 1.5,
        }}
      >
        {availableInterviews.map((interview) => {
          const isSelected = selectedInterviewId === interview.id;
          return (
            <Card
              key={interview.id}
              variant="outlined"
              sx={{
                borderColor: isSelected ? "primary.main" : undefined,
                borderWidth: isSelected ? 2 : 1,
              }}
            >
              <CardActionArea
                onClick={() => {
                  onSelectedInterviewIdChange(isSelected ? null : interview.id);
                }}
                data-testid={`interview-option-${interview.id}`}
              >
                <CardContent sx={{ py: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {isSelected ? (
                      <CheckCircleIcon color="primary" />
                    ) : (
                      <UncheckedIcon color="action" />
                    )}
                    <Box
                      sx={{
                        flex: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        {interviewLabelMap.get(interview.id) ??
                          "MHA Assessment"}
                      </Typography>
                      {interview.address && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <LocationOnIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {interview.address}
                            {interview.postalCode &&
                              `, ${interview.postalCode}`}
                          </Typography>
                        </Box>
                      )}
                      {interview.locationUnknown && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          Location to be confirmed
                        </Typography>
                      )}
                      {interview.timeRanges.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          <ScheduleIcon fontSize="small" color="action" />
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {interview.timeRanges.length} time slot
                            {interview.timeRanges.length !== 1 ? "s" : ""}
                          </Typography>
                        </Box>
                      )}
                      {interview.participants.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            flexWrap: "wrap",
                            mt: 0.5,
                          }}
                        >
                          {interview.participants.map((p) => (
                            <Chip
                              key={p.id}
                              label={p.userName || p.userEmail}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <MuiButton
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setShowCreateDialog(true);
          }}
          size="small"
        >
          Create Assessment
        </MuiButton>
      </Box>
      {/* Create Interview sub-dialog — reuses the real InterviewFormView */}
      {showCreateDialog && (
        <Dialog
          open
          onClose={() => {
            setShowCreateDialog(false);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Create Assessment
            <IconButton
              aria-label="Close"
              onClick={() => {
                setShowCreateDialog(false);
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <InterviewFormView
              formContextId={formContextId}
              onSave={() => void handleInterviewCreated()}
              onCancel={() => {
                setShowCreateDialog(false);
              }}
              apiClient={{
                ...api.interviews,
                createWorkItem: api.work.create,
                ensureGuestUser: api.users.ensureGuestUser,
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}
